import { useLocation, useNavigate, Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { ArrowRight, ArrowLeft, Newspaper, Package, Compass, Info } from 'lucide-react';
import Logo from '@/components/Logo';

const QUICK_LINKS = [
  { label: 'Home', path: '/', icon: ArrowLeft },
  { label: 'Newsroom', path: '/newsroom', icon: Newspaper },
  { label: 'Packages', path: '/packages', icon: Package },
  { label: 'Find Your Fit', path: '/services/find-your-fit', icon: Compass },
];

export default function PageNotFound({}) {
  const location = useLocation();
  const navigate = useNavigate();
  const loggedRef = useRef(false);

  useEffect(() => {
    if (loggedRef.current) return;
    loggedRef.current = true;
    // Self-heal mangled URLs: a trailing punctuation mark (e.g. a period or
    // comma from a pasted/emailed link) makes a valid route 404. Strip it and
    // silently redirect to the cleaned path instead of logging an error.
    const cleaned = location.pathname.replace(/[.,;:!?)"'’]+$/g, '');
    if (cleaned && cleaned !== location.pathname) {
      navigate(cleaned + location.search, { replace: true });
      return;
    }
    base44.functions.invoke('log404', {
      path: location.pathname,
      referrer: document.referrer || '',
    }).catch(() => {});
  }, [location.pathname]);

  const { data: authData, isFetched } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      try {
        const user = await base44.auth.me();
        return { user, isAuthenticated: true };
      } catch (error) {
        return { user: null, isAuthenticated: false };
      }
    }
  });

  const isAdmin = isFetched && authData.isAuthenticated && (authData.user?.role === 'admin' || authData.user?.role === 'super_admin');

  return (
    <div className="min-h-screen flex flex-col bg-showcase text-white relative overflow-hidden">
      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cobalt/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Header with logo */}
      <header className="relative z-10 px-6 py-6">
        <Logo />
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full text-center">
          {/* 404 */}
          <div className="mb-8">
            <span className="inline-block font-mono text-xs uppercase tracking-widest text-lilac mb-6">
              ERROR 404
            </span>
            <h1 className="font-heading text-[120px] sm:text-[160px] font-black leading-none text-gradient">
              404
            </h1>
            <div className="h-px w-24 bg-gradient-to-r from-transparent via-cobalt to-transparent mx-auto mt-6" />
          </div>

          {/* Message */}
          <div className="space-y-3 mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
              This page doesn't exist.
            </h2>
            <p className="text-mist text-base leading-relaxed">
              The page <code className="font-mono text-lilac text-sm px-1.5 py-0.5 bg-white/5 rounded">{location.pathname}</code> could not be found. It may have been moved, deleted, or never existed.
            </p>
          </div>

          {/* Admin note */}
          {isAdmin && (
            <div className="mb-8 p-4 bg-white/5 border border-white/10 rounded-lg text-left">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-cobalt/20 flex items-center justify-center">
                  <Info className="w-4 h-4 text-lilac" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-white">Admin Note</p>
                  <p className="text-sm text-mist leading-relaxed">
                    This 404 has been logged. You can view all 404 errors in the{' '}
                    <Link to="/admin/error-logs" className="text-lilac underline hover:text-white">
                      404 Monitor
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Primary CTA */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-cobalt hover:bg-cobalt/90 text-white text-sm font-semibold rounded-md transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Quick links */}
          <div className="mt-12">
            <p className="font-mono text-xs uppercase tracking-widest text-faded mb-4">
              Or try one of these
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {QUICK_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-mist border border-white/10 rounded-md hover:border-cobalt hover:text-white transition-colors"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-6 text-center">
        <p className="font-mono text-xs text-faded">
          Digital24 — PR Distribution Platform
        </p>
      </footer>
    </div>
  );
}